import {
  subDays as dateFnsSubDays,
  format as dateFnsFormat,
  isBefore as dateFnsIsBefore,
  isAfter as dateFnsIsAfter,
  isEqual as dateFnsIsEqual,
} from "date-fns";

interface OpeningHoursRange {
  from: string;
  to: string;
}

interface ExceptionDate {
  name: string;
  date: string;
  repeating: boolean;
}

type DayOpeningHoursType = "closed" | "openAllDay" | "fromTo";

class DayOpeningHours {
  private readonly dayName!: string;
  private readonly closed!: boolean;
  private readonly openAllDay!: boolean;
  private readonly type!: DayOpeningHoursType;
  private hoursStep = 30;
  private readonly ranges!: OpeningHoursRange[];

  constructor(
    private readonly openingHours: OpeningHoursModel,
    dayName: string,
    hoursStep: number,
    data:
      | {
          [key: string]: any;
        }
      | undefined,
  ) {
    this.dayName = dayName;
    this.closed = data?.["closed"] ?? false;
    this.openAllDay = data?.["openAllDay"] ?? false;
    this.hoursStep = hoursStep;
    this.ranges = data?.["ranges"] ?? [];
    this.type =
      this.closed === true
        ? "closed"
        : this.openAllDay === true
          ? "openAllDay"
          : "fromTo";
  }

  public getOpeningHours(dateTime: Date): string[] {
    if (this.type === "openAllDay") {
      const hours = generateAllDayOpeningHours(this.hoursStep);
      if (hours.length % 3 !== 0) {
        const remainingElements = 3 - (hours.length % 3);
        for (let i = 0; i < remainingElements; i++) {
          hours.push("null");
        }
      }
      return hours;
    }

    // get previous day opening hours
    const previousDayHasRemainingHours =
      this.openingHours.previousDayHasRemainingHours(
        this.dayName,
        dateFnsSubDays(dateTime, 1),
      ) ?? [];

    const dayRanges = this.ranges ?? [];
    const ranges = [...previousDayHasRemainingHours, ...dayRanges];

    const openingHoursList = generateTimeSlots(ranges[0].from, this.hoursStep);

    if (openingHoursList.length % 3 !== 0) {
      const remainingElements = openingHoursList.length % 3;
      for (let i = 0; i < 3 - remainingElements; i++) {
        openingHoursList.push("null");
      }
    }
    return openingHoursList;
  }

  public remainingHours(
    dateTime: Date,
    exceptions: ExceptionDate[] = [],
  ): OpeningHoursRange[] | undefined {
    try {
      const isException = this.openingHours.isExceptionDate(dateTime);
      if (isException) {
        return undefined;
      }
      if (this.type === "closed" || this.type === "openAllDay") {
        return undefined;
      }

      const ranges = this.ranges;
      const remaingHours: OpeningHoursRange[] = [];
      let nextDay = false;
      let passMidDay = false;

      for (let i = 0; i < ranges.length; i++) {
        const range = ranges[i];
        const fromHour = range.from;
        const toHour = range.to;

        if (toHour < fromHour && toHour > "00:00") {
          remaingHours.push({
            from: "00:00",
            to: toHour,
          });
          nextDay = true;
        } else if (fromHour < "00:00" && i > 0) {
          remaingHours.push({
            from: fromHour,
            to: toHour,
          });
          nextDay = true;
        } else if (nextDay) {
          remaingHours.push({
            from: fromHour,
            to: toHour,
          });
        } else if (passMidDay && toHour > "00:00" && toHour < "12:00") {
          remaingHours.push({
            from: "00:00",
            to: toHour,
          });
        }

        if (fromHour > "12:00" || toHour > "12:00") {
          passMidDay = true;
        }
      }
      return remaingHours.length > 0 ? remaingHours : undefined;
    } catch (error) {
      return undefined;
    }
  }

  public isPlaceOpenAt(
    date: Date,
    time?: string,
    exceptions: ExceptionDate[] = [],
  ): boolean {
    try {
      if (
        time === undefined ||
        time === null ||
        time === "" ||
        time === "null"
      ) {
        return false;
      }
      const previousDayHasRemainingHours =
        this.openingHours.previousDayHasRemainingHours(
          this.dayName,
          dateFnsSubDays(date, 1),
        ) ?? [];

      if (previousDayHasRemainingHours.length > 0) {
        for (const pdRange of previousDayHasRemainingHours) {
          const parsedFromTime = parseHourAndMinute(pdRange.from);
          const fromHour = parsedFromTime.hours;
          const fromMinutes = parsedFromTime.minutes;
          const parsedToTime = parseHourAndMinute(pdRange.to);
          const toHour = parsedToTime.hours;
          const toMinutes = parsedToTime.minutes;
          const pasedBookingTime = parseHourAndMinute(time);
          const bookingHour = pasedBookingTime.hours;
          const bookingMinutes = pasedBookingTime.minutes;
          const fromDate = createZeroDate(fromHour, fromMinutes);
          const toDate = createZeroDate(toHour, toMinutes);
          const bookingDate = createZeroDate(bookingHour, bookingMinutes);
          if (getDate(date) === getDate(new Date())) {
            const now = new Date();
            if (
              (dateFnsIsAfter(bookingDate, now) ||
                time === fromHour.toString()) &&
              dateFnsIsAfter(bookingDate, fromDate) &&
              dateFnsIsBefore(bookingDate, toDate)
            ) {
              return true;
            }
          } else if (
            (dateFnsIsAfter(bookingDate, fromDate) ||
              dateFnsIsEqual(bookingDate, fromDate)) &&
            dateFnsIsBefore(bookingDate, toDate)
          ) {
            return true;
          }
        }
      }
      if (this.closed) {
        return false;
      }
      const isException = this.openingHours.isExceptionDate(date);
      if (isException) {
        return false;
      }
      if (this.openAllDay) {
        if (getDate(date) === getDate(new Date())) {
          const now = new Date();
          if (time >= getTimeString(now)) {
            return true;
          }
        } else {
          return true;
        }
      } else if (this.type === "fromTo") {
        let isNextDay = false;
        for (const hoursRange of this.ranges) {
          if (isNextDay) {
            return false;
          }
          let dayOpeningHourTo = hoursRange.to;
          if (dayOpeningHourTo === "00:00") {
            dayOpeningHourTo = "23:59";
            isNextDay = true;
          }
          const parsedFromTime = parseHourAndMinute(hoursRange.from);
          const fromHour = parsedFromTime.hours;
          const fromMinutes = parsedFromTime.minutes;
          const parsedToTime = parseHourAndMinute(dayOpeningHourTo);
          let toHour = parsedToTime.hours;
          const toMinutes = parsedToTime.minutes;
          if (toHour < fromHour) {
            toHour = toHour + 24;
          }
          const pasedBookingTime = parseHourAndMinute(time);
          const bookingHour = pasedBookingTime.hours;
          const bookingMinutes = pasedBookingTime.minutes;
          const fromDate = createZeroDate(fromHour, fromMinutes);
          const toDate = createZeroDate(toHour, toMinutes);
          const bookingDate = createZeroDate(bookingHour, bookingMinutes);
          if (time === "00:00") {
          }
          if (getDate(date) === getDate(new Date())) {
            const now = new Date();
            if (
              time >= getTimeString(now) &&
              (dateFnsIsAfter(bookingDate, fromDate) ||
                dateFnsIsEqual(bookingDate, fromDate)) &&
              dateFnsIsBefore(bookingDate, toDate)
            ) {
              return true;
            }
          } else if (
            (dateFnsIsAfter(bookingDate, fromDate) ||
              dateFnsIsEqual(bookingDate, fromDate)) &&
            dateFnsIsBefore(bookingDate, toDate)
          ) {
            return true;
          }
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  public isPlaceOpenAtDate(date: Date): boolean {
    // TODO: check if previous day has remaining hours
    if (this.type === "closed") {
      return false;
    }
    // * check if date is exception date
    const isException = this.openingHours.isExceptionDate(date);
    if (isException) {
      return false;
    }
    return true;
  }
}

export class OpeningHoursModel {
  private readonly monday!: DayOpeningHours;
  private readonly tuesday!: DayOpeningHours;
  private readonly wednesday!: DayOpeningHours;
  private readonly thursday!: DayOpeningHours;
  private readonly friday!: DayOpeningHours;
  private readonly saturday!: DayOpeningHours;
  private readonly sunday!: DayOpeningHours;
  private hoursStep = 30;
  private readonly exceptions: ExceptionDate[] = [];

  constructor(
    openingHours: OpeningHours | undefined | null,
    hoursStep: number = 30,
  ) {
    this.monday = new DayOpeningHours(
      this,
      "monday",
      hoursStep,
      openingHours?.monday,
    );
    this.tuesday = new DayOpeningHours(
      this,
      "tuesday",
      hoursStep,
      openingHours?.tuesday,
    );
    this.wednesday = new DayOpeningHours(
      this,
      "wednesday",
      hoursStep,
      openingHours?.wednesday,
    );
    this.thursday = new DayOpeningHours(
      this,
      "thursday",
      hoursStep,
      openingHours?.thursday,
    );
    this.friday = new DayOpeningHours(
      this,
      "friday",
      hoursStep,
      openingHours?.friday,
    );
    this.saturday = new DayOpeningHours(
      this,
      "saturday",
      hoursStep,
      openingHours?.saturday,
    );
    this.sunday = new DayOpeningHours(
      this,
      "sunday",
      hoursStep,
      openingHours?.sunday,
    );
    this.exceptions = (openingHours?.exceptions ??
      ([] as ExceptionDate[])) as any;
    this.hoursStep = hoursStep;
  }

  public getDayOpeningHours(dateTime: Date): string[] {
    try {
      const day = getDateEnglishName(dateTime);
      switch (day) {
        case "monday":
          return this.monday.getOpeningHours(dateTime);
        case "tuesday":
          return this.tuesday.getOpeningHours(dateTime);
        case "wednesday":
          return this.wednesday.getOpeningHours(dateTime);
        case "thursday":
          return this.thursday.getOpeningHours(dateTime);
        case "friday":
          return this.friday.getOpeningHours(dateTime);
        case "saturday":
          return this.saturday.getOpeningHours(dateTime);
        case "sunday":
          return this.sunday.getOpeningHours(dateTime);
        default:
          return [];
      }
    } catch (error) {
      return [];
    }
  }

  public isPlaceOpenAt(
    date?: Date | null | undefined,
    time?: string | null | undefined,
  ) {
    try {
      if (!date || !time) {
        return false;
      }
      const day = getDateEnglishName(date);
      switch (day) {
        case "monday":
          return this.monday.isPlaceOpenAt(date, time, this.exceptions);
        case "tuesday":
          return this.tuesday.isPlaceOpenAt(date, time, this.exceptions);
        case "wednesday":
          return this.wednesday.isPlaceOpenAt(date, time, this.exceptions);
        case "thursday":
          return this.thursday.isPlaceOpenAt(date, time, this.exceptions);
        case "friday":
          return this.friday.isPlaceOpenAt(date, time, this.exceptions);
        case "saturday":
          return this.saturday.isPlaceOpenAt(date, time, this.exceptions);
        case "sunday":
          return this.sunday.isPlaceOpenAt(date, time, this.exceptions);
        default:
          return false;
      }
    } catch (error) {
      return false;
    }
  }

  public previousDayHasRemainingHours(
    dayName: string,
    dateTime: Date,
  ): OpeningHoursRange[] | undefined {
    try {
      switch (dayName) {
        case "monday":
          return this.sunday.remainingHours(dateTime);
        case "tuesday":
          return this.monday.remainingHours(dateTime);
        case "wednesday":
          return this.tuesday.remainingHours(dateTime);
        case "thursday":
          return this.wednesday.remainingHours(dateTime);
        case "friday":
          return this.thursday.remainingHours(dateTime);
        case "saturday":
          return this.friday.remainingHours(dateTime);
        case "sunday":
          return this.saturday.remainingHours(dateTime);
        default:
          return undefined;
      }
    } catch (error) {
      return undefined;
    }
  }

  public isPlaceOpenAtDate(date: Date): boolean {
    try {
      const day = getDateEnglishName(date);
      switch (day) {
        case "monday":
          return this.monday.isPlaceOpenAtDate(date);
        case "tuesday":
          return this.tuesday.isPlaceOpenAtDate(date);
        case "wednesday":
          return this.wednesday.isPlaceOpenAtDate(date);
        case "thursday":
          return this.thursday.isPlaceOpenAtDate(date);
        case "friday":
          return this.friday.isPlaceOpenAtDate(date);
        case "saturday":
          return this.saturday.isPlaceOpenAtDate(date);
        case "sunday":
          return this.sunday.isPlaceOpenAtDate(date);
        default:
          return false;
      }
    } catch (error) {
      return false;
    }
  }

  public isExceptionDate(date: Date): boolean {
    // console.log('isExceptionDate', this.exceptions, date);
    if (this.exceptions.length === 0) {
      return false;
    }
    const exceptionDate = this.exceptions.find((expDate) => {
      const expDateDateTime = new Date(expDate.date);
      if (expDate.repeating === true) {
        if (
          expDateDateTime.getDate() === date.getDate() &&
          expDateDateTime.getMonth() === date.getMonth()
        ) {
          return true;
        }
      } else {
        if (
          expDateDateTime.getDate() === date.getDate() &&
          expDateDateTime.getMonth() === date.getMonth() &&
          expDateDateTime.getFullYear() === date.getFullYear()
        ) {
          return true;
        }
      }
      return false;
    });
    if (exceptionDate) {
      return true;
    }
    return false;
  }
}

// method to get the english name of a date
function getDateEnglishName(data: Date): string {
  const day = data.getDay();
  switch (day) {
    case 0:
      return "sunday";
    case 1:
      return "monday";
    case 2:
      return "tuesday";
    case 3:
      return "wednesday";
    case 4:
      return "thursday";
    case 5:
      return "friday";
    case 6:
      return "saturday";
    default:
      return "Invalid day";
  }
}

// method to generate all day opening hours
function generateAllDayOpeningHours(minutesBetweenHours: number): string[] {
  const allDayOpeningHours: string[] = [];

  for (let i = 0; i < 24 * 60; i += minutesBetweenHours) {
    const hours = i / 60;
    const minutes = i % 60;
    const formattedHours = Math.floor(hours).toString().padStart(2, "0");
    const formattedMinutes = minutes.toString().padStart(2, "0");
    allDayOpeningHours.push(`${formattedHours}:${formattedMinutes}`);
  }

  return allDayOpeningHours;
}

// method to generate time slots
function generateTimeSlots(
  from: string,
  minutesBetweenHours: number,
): string[] {
  const timeSlotes: string[] = [];
  let hours = parseInt(from.split(":")[0]);
  let minutes = parseInt(from.split(":")[1]);

  while (hours < 24) {
    const formattedHours = Math.floor(hours).toString().padStart(2, "0");
    const formattedMinutes = minutes.toString().padStart(2, "0");
    timeSlotes.push(`${formattedHours}:${formattedMinutes}`);

    minutes += minutesBetweenHours;
    if (minutes >= 60) {
      hours = hours + Math.floor(minutes / 60);
      minutes = minutes % 60;
    }
  }

  return timeSlotes;
}

// method to parse hour and minute
function parseHourAndMinute(time?: string): {
  hours: number;
  minutes: number;
} {
  let startHour = 0;
  let startMinute = 0;
  if (time !== undefined && time !== null && time !== "") {
    try {
      startHour = parseInt(time.split(":")[0]);
    } catch (error) {
      // do nothing
    }
    try {
      startMinute = parseInt(time.split(":")[1]);
    } catch (error) {
      // do nothing
    }
  }
  return {
    hours: startHour,
    minutes: startMinute,
  };
}

// method to create a zero date
function createZeroDate(hours: number, minutes: number): Date {
  const zeroDate = new Date(0);
  zeroDate.setUTCHours(hours, minutes, 0, 0);
  return zeroDate;
}

function getDate(date: Date): string {
  return dateFnsFormat(date, "yyyy-MM-dd");
}

function getTimeString(date: Date): string {
  return dateFnsFormat(date, "HH:mm");
}
